import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ApiClient, useFetch, useMutation } from '../api';

type GetApplicationVersions =
  ApiClient['v2']['workspaces'][':workspaceId']['applications'][':applicationId']['versions']['$get'];
export type ApplicationVersionListResponse = InferResponseType<GetApplicationVersions, 200>;

type CreateApplicationVersion =
  ApiClient['v2']['workspaces'][':workspaceId']['applications'][':applicationId']['versions']['$post'];
export type CreateApplicationVersionRequest = InferRequestType<CreateApplicationVersion>['json'];
type CreateApplicationVersionResponse = InferResponseType<CreateApplicationVersion, 201>;

export function useApplicationVersions(workspaceId: number, applicationId: number) {
  return useFetch<ApplicationVersionListResponse>(
    `workspaces/${workspaceId}/applications/${applicationId}/versions`,
    (client) =>
      client.v2.workspaces[':workspaceId'].applications[':applicationId'].versions.$get({
        param: { workspaceId: workspaceId.toString(), applicationId: applicationId.toString() },
        query: { limit: '1000', sort: ['-id'] },
      })
  );
}

export function useAddApplicationVersion(
  workspaceId: number,
  applicationId: number,
  actionOnErr: (error?: unknown) => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/applications/${applicationId}/versions`;

  const addVersionMutation = useMutation<
    CreateApplicationVersionResponse,
    CreateApplicationVersionRequest
  >(
    listCacheKey,
    (client, data) =>
      client.v2.workspaces[':workspaceId'].applications[':applicationId'].versions.$post({
        param: { workspaceId: workspaceId.toString(), applicationId: applicationId.toString() },
        json: data,
      }),
    actionOnErr,
    { suppressToast: true } // error is shown inline in the dialog
  );

  return useCallback(
    async (data: CreateApplicationVersionRequest) => {
      const result = await addVersionMutation(data);
      if (result !== undefined) {
        await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      }
      return result;
    },
    [addVersionMutation, mutate, listCacheKey]
  );
}
